import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerProps,
  FormControl,
  FormLabel,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { usePostVendorMutation } from 'api';
import { PostVendorRequest } from 'api/bindings/PostVendorRequest';
import { AppInput, FormButtonGroup } from 'features';
import { useCallback } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

export interface CreateVendorDrawerProps
  extends Omit<DrawerProps, 'onSubmit' | 'children'> {
  disallowedVendorNames: string[] | undefined;
}

interface Form extends PostVendorRequest {}

export const CreateVendorDrawer = (props: CreateVendorDrawerProps) => {
  const { disallowedVendorNames = [], ...rest } = props;

  // Fetches
  const [postVendor] = usePostVendorMutation();

  // State
  const toast = useToast();
  const form = useForm<Form>({
    mode: 'all',
    defaultValues: {},
  });

  const { reset, watch, control, register, handleSubmit } = form;
  const vendorName = watch('name');

  const isValidName = useCallback(
    (name) =>
      (disallowedVendorNames.some(
        (x) => x.toLowerCase() === name.toLowerCase()
      ) &&
        `Name ${name} is not available`) ||
      undefined,
    [disallowedVendorNames]
  );

  const onSubmit = useCallback(
    async (form: Form) => {
      try {
        const result = await postVendor(form);
        if ('error' in result) {
          toast({
            title: `Failed to post vendor ${form.name}`,
            description: (result.error as any).data?.message as string,
            status: 'error',
          });
          return;
        }
        toast({
          title: `Vendor ${form.name} posted!`,
          status: 'success',
        });
        reset();
        rest.onClose();
      } catch {}
    },
    [postVendor, reset, rest, toast]
  );

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Drawer {...rest}>
          <DrawerContent>
            <DrawerHeader>Create Vendor: {vendorName}</DrawerHeader>
            <DrawerBody>
              <Stack spacing={4}>
                <FormControl>
                  <FormLabel htmlFor="name">Name:</FormLabel>
                  <Controller
                    name="name"
                    control={control}
                    rules={{
                      required: 'Name is required',
                      minLength: {
                        value: 3,
                        message: 'Name must be at least 3 characters.',
                      },
                      maxLength: {
                        value: 256,
                        message: 'Name cannot exceed 256 characters.',
                      },
                      validate: {
                        isValidName,
                      },
                    }}
                    render={(f) => <AppInput id="name" {...(f.field as any)} />}
                  />
                </FormControl>
              </Stack>
            </DrawerBody>
            <DrawerFooter>
              <FormButtonGroup
                onConfirm={handleSubmit(onSubmit)}
                onCancel={() => {
                  reset();
                  rest.onClose();
                }}
              />
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </form>
    </FormProvider>
  );
};
